
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Game } from '@/types/game';
import { Card } from '@/components/ui/card';
import { Play, Maximize2, ArrowLeft, Maximize } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { useFullscreen } from '@/hooks/useFullscreen';

interface GamePlayerProps {
  game: Game;
  isPlaying: boolean;
  onPlayClick: () => void;
}

const GamePlayer: React.FC<GamePlayerProps> = ({ game, isPlaying, onPlayClick }) => {
  const [gameLoaded, setGameLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const { isFullscreen, toggleFullscreen, isSupported: fullscreenSupported } = useFullscreen({
    onEnter: () => console.log('Entered fullscreen mode'),
    onExit: () => console.log('Exited fullscreen mode'),
    onError: (error) => console.warn('Fullscreen error:', error)
  });

  const handleGameLoad = useCallback(() => {
    console.log('Game loaded successfully');
    setGameLoaded(true);
  }, []);

  const handleFullscreenToggle = useCallback(() => {
    if (gameContainerRef.current) {
      toggleFullscreen(gameContainerRef.current);
    }
  }, [toggleFullscreen]);

  // Load game iframe when playing starts (desktop only)
  useEffect(() => {
    if (isPlaying && !isMobile && iframeRef.current && !iframeRef.current.src) {
      console.log('Loading desktop game:', game.gameUrl);
      iframeRef.current.src = game.gameUrl;
    }
  }, [isPlaying, game.gameUrl, isMobile]);

  return (
    <Card className="overflow-hidden shadow-xl border-0 bg-white/70 backdrop-blur-sm">
      <div 
        ref={gameContainerRef}
        className={`relative bg-gradient-to-br from-gray-900 via-gray-800 to-black ${
          isMobile ? 'aspect-video' : 'aspect-video lg:aspect-[16/10]'
        }`}
      >
        {isPlaying && !isMobile ? (
          <>
            {/* Desktop Fullscreen Button - Only show when game is loaded and fullscreen is supported */}
            {gameLoaded && fullscreenSupported && (
              <div className="absolute top-4 right-4 z-50">
                <Button
                  onClick={handleFullscreenToggle}
                  variant="ghost"
                  size="sm"
                  className="bg-black/80 text-white hover:bg-black/90 backdrop-blur-sm border border-white/30"
                  title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                >
                  <Maximize className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Loading Indicator */}
            {!gameLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-30">
                <div className="flex flex-col items-center space-y-6">
                  <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-white text-xl font-medium">Loading game...</p>
                </div>
              </div>
            )}
            
            {/* Desktop Game Iframe */}
            <iframe
              ref={iframeRef}
              className="w-full h-full transition-opacity duration-500 border-0 opacity-100"
              title={game.title}
              onLoad={handleGameLoad}
              onError={(e) => console.error('Desktop iframe load error:', e)}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{ 
                display: 'block',
                background: 'black',
                border: 'none',
                outline: 'none'
              }}
            />
          </>
        ) : (
          // Game Preview/Thumbnail
          <div className="absolute inset-0 flex items-center justify-center group cursor-pointer">
            <img
              src={game.thumbnail}
              alt={game.title}
              className="w-full h-full object-cover"
              loading="eager"
            />
            <button
              onClick={onPlayClick}
              className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/30 transition-all duration-300"
            >
              <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-full flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform duration-300">
                <Play className="w-10 h-10 md:w-12 md:h-12 text-blue-600 ml-1" fill="currentColor" />
              </div>
              {isMobile && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-black/80 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm border border-white/30">
                    <Maximize2 className="w-4 h-4 inline mr-2" />
                    Play Game
                  </div>
                </div>
              )}
            </button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default GamePlayer;
